import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "@emotion/styled";
import { wrap } from "popmotion";

import { ThemeContext, UserContext } from "../contexts/Context";

const PROMOTION_SHOWN_KEY = "selfOrderPromotionShown";

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: { zIndex: 1, x: 0, opacity: 1 },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const Promotion = () => {
  const { ads, title } = React.useContext(UserContext);
  const themeContext = React.useContext(ThemeContext);
  const [[page, direction], setPage] = useState([0, 0]);
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  const finalAds = useMemo(() => {
    if (!ads || ads.length === 0) return [];

    return ads.filter((ad) => {
      if (!title || !ad.titles || ad.titles.length === 0) return true;
      return ad.titles.indexOf(title) > -1;
    });
  }, [ads, title]);

  useEffect(() => {
    if (finalAds.length === 0) return;

    try {
      if (window.sessionStorage.getItem(PROMOTION_SHOWN_KEY)) return;
      // Mark it when opened so refreshing Menu/Category does not open it again.
      window.sessionStorage.setItem(PROMOTION_SHOWN_KEY, "true");
    } catch (error) {
      // The modal can still be shown when storage is unavailable.
    }

    setShow(true);
  }, [finalAds.length]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const paginate = useCallback((newDirection) => {
    setPage(([currentPage]) => [currentPage + newDirection, newDirection]);
  }, []);

  useEffect(() => {
    clearTimer();
    if (!show || finalAds.length < 2) return undefined;

    timerRef.current = setTimeout(() => paginate(1), 5000);
    return clearTimer;
  }, [page, show, finalAds.length, clearTimer, paginate]);

  const closePromotion = () => {
    clearTimer();
    setShow(false);
  };

  if (!show || finalAds.length === 0) return null;

  const imageIndex = wrap(0, finalAds.length, page);

  return (
    <ModalOverlay onClick={closePromotion}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-label="Promotions"
        onClick={(event) => event.stopPropagation()}
      >
        <CloseButton type="button" aria-label="Close promotions" onClick={closePromotion}>
          <CloseIcon aria-hidden="true">&times;</CloseIcon>
        </CloseButton>

        <AnimatePresence initial={false} custom={direction}>
          <PromotionContainer
            key={page}
            src={`https://ireachfnb.com${finalAds[imageIndex].img}`}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag={finalAds.length > 1 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragStart={clearTimer}
            onDragEnd={(event, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
          />
        </AnimatePresence>

        {finalAds.length > 1 && (
          <DotWrapper>
            {finalAds.map((ad, index) => (
              <Dot
                key={ad._id || ad.img || index}
                type="button"
                aria-label={`Show promotion ${index + 1}`}
                active={imageIndex === index}
                color={themeContext.primaryColor}
                onClick={() => setPage(([currentPage]) => [index, index - currentPage])}
              />
            ))}
          </DotWrapper>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.58);
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  height: 75vh;
  max-height: 680px;
  min-height: 320px;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
`;

const PromotionContainer = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  color: white;
  background: rgba(20, 20, 20, 0.72);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.06);
  }

  &:focus-visible {
    outline: 3px solid white;
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const CloseIcon = styled.span`
  display: block;
  font-size: 32px;
  font-weight: 300;
  line-height: 1.5;
`;

const DotWrapper = styled.div`
  display: flex;
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;
  z-index: 3;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${(props) => props.active ? props.color : "gray"};
  margin: 0 0.5rem;
  cursor: pointer;
`;

export default Promotion;
